import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TaxpayerCertificate, TaxpayerCertificateForm } from '../../../types/hacienda/TaxpayerCertificateTypes';

interface TaxpayerCertificateState {
    form: TaxpayerCertificateForm;
    certificates: TaxpayerCertificate[];
    isLoading: boolean;
    isSubmitting: boolean;
    error: string | null;
    uploadModalOpen: boolean;
    editModalOpen: boolean;
    certificateToEdit: TaxpayerCertificate | null;
}

const initialState: TaxpayerCertificateState = {
    certificates: [],
    isLoading: false,
    isSubmitting: false,
    error: null,
    form: {
        fileType: null,
        fileName: ''
    },
    uploadModalOpen: false,
    editModalOpen: false,
    certificateToEdit: null,
};

const taxpayerCertificateSlice = createSlice({
    name: 'taxpayerCertificate',
    initialState,
    reducers: {
        setCertificates(state, action: PayloadAction<TaxpayerCertificate[]>) {
            state.certificates = action.payload;
            state.error = null;
        },
        addOrUpdateCertificate(state, action: PayloadAction<TaxpayerCertificate>) {
            const updated = action.payload;
            const index = state.certificates.findIndex(c => c.fileType === updated.fileType);
            if (index !== -1) {
                state.certificates[index] = updated;
            } else {
                state.certificates.push(updated);
            }
        },
        updateCertificateFormField(
            state,
            action: PayloadAction<{ field: keyof TaxpayerCertificateForm; value: any }>
        ) {
            const { field, value } = action.payload;
            state.form = {
                ...state.form,
                [field]: value,
            };
        },
        removeCertificate(state, action: PayloadAction<'CERTIFICATE' | 'PRIVATE_KEY'>) {
            state.certificates = state.certificates.filter(c => c.fileType !== action.payload);
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
        setSubmitting(state, action: PayloadAction<boolean>) {
            state.isSubmitting = action.payload;
        },
        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },
        resetCertificates(state) {
            state.certificates = [];
            state.isLoading = false;
            state.isSubmitting = false;
            state.error = null;
        },
        setUploadModalOpen(state, action: PayloadAction<boolean>) {
            state.uploadModalOpen = action.payload;
        },
        setEditModalOpen(state, action: PayloadAction<boolean>) {
            state.editModalOpen = action.payload;
        },
        setCertificateToEdit(state, action: PayloadAction<TaxpayerCertificate | null>) {
            state.certificateToEdit = action.payload;
        },
        clearCertificateToEdit(state) {
            state.certificateToEdit = null;
        },
    },
});

export const {
    setCertificates,
    addOrUpdateCertificate,
    updateCertificateFormField,
    removeCertificate,
    setLoading,
    setSubmitting,
    setError,
    resetCertificates,
    setUploadModalOpen,
    setEditModalOpen,
    setCertificateToEdit,
    clearCertificateToEdit,
} = taxpayerCertificateSlice.actions;

export default taxpayerCertificateSlice.reducer;
